import React from 'react';
import { API_BASE, toUrl } from '../lib/config';

type Row = {
  id: string;
  page: string;
  logoUrl?: string | null;
  kicker?: string | null;
  titleMain?: string | null;
  titleAccent?: string | null;
  description?: string | null;
  isActive: boolean;
  updatedAt: string;
};

type MediaOpt = { id?: string; name: string; url: string };

const PageDoctorsHeroYonetim: React.FC = () => {
  const PAGE = 'hekimler';

  const resolveMedia = (u?: string | null) => {
    if (!u) return '';
    const uuidRe = /^[0-9a-fA-F-]{36}$/;
    const path = uuidRe.test(u) ? `/api/media/${u}` : u;
    return toUrl(path);
  };
  const normalizeMediaForSave = (u?: string | null) => {
    if (!u) return null;
    const m = u.match(/([0-9a-fA-F-]{36})$/);
    return m ? m[1] : u;
  };
  const ImgWithFallback: React.FC<{ src?: string | null; alt: string; height?: number }> = ({ src, alt, height = 56 }) => {
    const baseIn = src || '';
    const m = baseIn.match(/([0-9a-fA-F-]{36})$/);
    const raw = m ? `/api/media/${m[1]}` : baseIn;
    const pref = toUrl(raw);
    const [currentSrc, setCurrentSrc] = React.useState(pref);
    return (
      <img
        src={currentSrc}
        alt={alt}
        style={{ height, objectFit: 'contain', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff' }}
        crossOrigin="anonymous"
        onError={(e) => {
          const el = e.currentTarget as HTMLImageElement;
          const cur = el.getAttribute('src') || '';
          const next = cur === pref ? raw : pref;
          if (next !== cur) el.src = next;
        }}
      />
    );
  };

  const [items, setItems] = React.useState<Row[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const [selectedId, setSelectedId] = React.useState('');
  const [eLogo, setELogo] = React.useState('');
  const [eKicker, setEKicker] = React.useState('');
  const [eMain, setEMain] = React.useState('');
  const [eAccent, setEAccent] = React.useState('');
  const [eDesc, setEDesc] = React.useState('');
  const [eActive, setEActive] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [toast, setToast] = React.useState<{ show: boolean; text: string }>({ show: false, text: '' });

  const [mediaOpts, setMediaOpts] = React.useState<MediaOpt[]>([]);

  const fetchItems = React.useCallback(async () => {
    try {
      setLoading(true); setError('');
      const res = await fetch(toUrl(`/api/page-doctors-hero/admin/${PAGE}`));
      if (!res.ok) throw new Error('Kayıtlar alınamadı');
      const data = await res.json();
      setItems(data || []);
      const initial = (data && data[0]) || null;
      if (initial) {
        setSelectedId(initial.id);
        setELogo(initial.logoUrl || '');
        setEKicker(initial.kicker || '');
        setEMain(initial.titleMain || '');
        setEAccent(initial.titleAccent || '');
        setEDesc(initial.description || '');
        setEActive(!!initial.isActive);
      } else {
        setSelectedId(''); setELogo(''); setEKicker(''); setEMain(''); setEAccent(''); setEDesc(''); setEActive(true);
      }
    } catch (e: any) { setError(e.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setLoading(false); }
  }, []);

  const fetchMedia = React.useCallback(async () => {
    try {
      const res = await fetch(toUrl('/api/media/admin'));
      if (!res.ok) return;
      const data = await res.json();
      setMediaOpts((data || []).map((m: any) => ({ id: m.id, name: m.name || m.originalName || m.id || m.url, url: m.url || `/api/media/${m.id}` })));
    } catch { /* ignore */ }
  }, []);

  React.useEffect(() => { fetchItems(); fetchMedia(); }, [fetchItems, fetchMedia]);

  const onSelect = (id: string) => {
    setSelectedId(id);
    const it = items.find(x => x.id === id);
    if (it) {
      setELogo(it.logoUrl || '');
      setEKicker(it.kicker || '');
      setEMain(it.titleMain || '');
      setEAccent(it.titleAccent || '');
      setEDesc(it.description || '');
      setEActive(!!it.isActive);
    }
  };

  const onSave = async () => {
    if (!selectedId) { setError('Düzenlenecek kayıt yok'); return; }
    try {
      setSaving(true); setError('');
      const body: any = {
        logoUrl: normalizeMediaForSave(eLogo),
        kicker: eKicker || null,
        titleMain: eMain || null,
        titleAccent: eAccent || null,
        description: eDesc || null,
        isActive: !!eActive,
      };
      const res = await fetch(toUrl(`/api/page-doctors-hero/admin/${selectedId}`), { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('Güncelleme başarısız');
      await fetchItems();
      setToast({ show: true, text: 'Başarılı' });
      window.setTimeout(() => setToast({ show: false, text: '' }), 2000);
    } catch (e: any) { setError(e.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setSaving(false); }
  };

  return (
    <div className="admin-page" style={{ padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>Hekimler Hero (Doctors Hero)</h2>
        <div style={{ fontSize: 12, color: '#6b7280' }}>Kayıt: {items.length}</div>
      </div>

      <div className="card" style={{ padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontWeight: 600 }}>Alanlar</div>
          <button onClick={fetchItems} className="btn">Yenile</button>
        </div>

        {items.length > 1 && (
          <div style={{ marginBottom: 12 }}>
            <label className="form-label" style={{ display: 'block', marginBottom: 6 }}>Kayıt Seç</label>
            <select className="input" value={selectedId} onChange={(e) => onSelect(e.target.value)}>
              {items.map(it => (
                <option key={it.id} value={it.id}>{it.page} – {it.titleMain || it.titleAccent || it.kicker || it.id}</option>
              ))}
            </select>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Logo</label>
            <div style={{ display: 'grid', gap: 6 }}>
              <select className="input" value={eLogo} onChange={(e) => setELogo(e.target.value)}>
                <option value="">Seçiniz</option>
                {mediaOpts.map(m => (
                  <option key={(m.id || m.url)} value={(m.id || m.url)}>{m.name}</option>
                ))}
              </select>
              {eLogo && (<ImgWithFallback src={eLogo} alt="logo" height={56} />)}
            </div>
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Kicker</label>
            <input className="input" value={eKicker} onChange={(e) => setEKicker(e.target.value)} />
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Başlık (Main)</label>
            <input className="input" value={eMain} onChange={(e) => setEMain(e.target.value)} />
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Başlık (Accent)</label>
            <input className="input" value={eAccent} onChange={(e) => setEAccent(e.target.value)} />
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Açıklama</label>
            <textarea className="input" rows={4} value={eDesc} onChange={(e) => setEDesc(e.target.value)} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input id="act" type="checkbox" checked={eActive} onChange={(e) => setEActive(e.target.checked)} />
            <label htmlFor="act">Aktif</label>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={onSave} disabled={saving || !selectedId} className="btn btn-primary">{saving ? 'Kaydediliyor...' : 'Kaydet'}</button>
        </div>

        {error && <div style={{ marginTop: 8, color: '#b00020' }}>{error}</div>}
      </div>
      {toast.show && (
        <div role="status" aria-live="polite" style={{ position: 'fixed', right: 16, bottom: 16, background: '#16a34a', color: '#fff', padding: '10px 14px', borderRadius: 8, boxShadow: '0 10px 20px rgba(0,0,0,0.15)' }}>
          {toast.text}
        </div>
      )}
    </div>
  );
};

export default PageDoctorsHeroYonetim;
